import 'cleave.js/dist/addons/cleave-phone.us';
import {CleaveOptions} from 'cleave.js/options';
import Cleave from 'cleave.js/react';
import {cx} from 'emotion';
import React, {useEffect, useState, useMemo} from 'react';
import {useDropzone} from 'react-dropzone';
import {
  HtmlInputrops,
  Input,
  StrictInputProps,
  Message,
  Form,
} from 'semantic-ui-react';
import {AnyObject} from '../../types';

import {
  defaultValueConverter,
  ValueConverter,
  currencyValueConverter,
  decimalValueConverter,
} from '../value-converters';

type InputAdditionalProps = {
  valueConverter?: ValueConverter;
};

export type InputAdapterProps = StrictInputProps &
  HtmlInputrops &
  InputAdditionalProps;

export const InputAdapter = ({input, meta, ...rest}) => {
  let {valueConverter, ...additionalProps} = rest as InputAdditionalProps;
  const converter = (valueConverter || defaultValueConverter) as ValueConverter;

  const [value, setValue] = useState(converter.format(input.value));

  // if formatting the external value does not match
  // the internal value, update internal value
  useEffect(() => {
    const shouldSetValue = !converter.equals(input.value, value);
    if (shouldSetValue) {
      setValue(converter.format(input.value));
    }
  }, [converter, input.value, value]);

  return (
    <Input
      {...input}
      value={value}
      {...additionalProps}
      onChange={(event, {value}) => {
        setValue(value);
        input.onChange(converter.parseInput(value));
      }}
      onFocus={(event) => {
        input.onFocus(event);
        event.target.select();
      }}
      onBlur={(event) => {
        setValue(converter.format(input.value));
        input.onBlur(event);
      }}
    />
  );
};

export const InputCurrencyAdapter = ({input, meta, ...rest}) => {
  const props: AnyObject = {...rest, valueConverter: currencyValueConverter};

  return (
    <InputAdapter
      input={input}
      meta={meta}
      icon="dollar"
      iconPosition="left"
      {...props}
      className={cx('input-currency', props.className)}
    />
  );
};

export const InputFileUrlAdapter = ({input, meta, ...rest}) => {
  const {onDrop} = rest;
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<any>(null);
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setFile(file);
      setUploading(true);
      const uri = await onDrop(acceptedFiles[0]);
      input.onChange(uri);
      setUploading(false);
    },
  });
  const uploader = useMemo(
    () => (
      <div {...getRootProps()}>
        <Message
          icon="upload"
          info={isDragActive}
          content={
            <>
              <input {...getInputProps()} />
              <p>Drag file here, or click to select files</p>
            </>
          }
        />
      </div>
    ),
    [getInputProps, getRootProps, isDragActive]
  );
  const thumbs = useMemo(
    () =>
      file && (
        <div>
          {/* <Image src={file.preview} size="small" bordered /> */}
          <Form.Button
            type="button"
            size="mini"
            basic
            color="black"
            onClick={() => {
              setFile(null);
              input.onChange('');
            }}
          >
            Remove
          </Form.Button>
        </div>
      ),
    [file, input]
  );
  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      file && URL.revokeObjectURL(file.preview);
    },
    [file]
  );
  return (
    <div className="ui field">
      {uploading ? 'Loading...' : file ? thumbs : uploader}
    </div>
  );
};

export const InputDecimalAdapter = ({input, meta, ...rest}) => {
  const props = {...rest, valueConverter: decimalValueConverter};
  return (
    <InputAdapter
      input={input}
      meta={meta}
      icon="percent"
      iconPosition="right"
      {...props}
    />
  );
};

export type InputMaskedAdapterProps = InputAdapterProps & {
  options?: CleaveOptions;
};

export const InputMaskedAdapter = ({input, meta, ...rest}) => {
  const {className, ...inputProps} = rest;
  return (
    <div className={cx('ui input', className)}>
      <Cleave {...input} {...inputProps} />
    </div>
  );
};
